/**
 * Phase 2 — entity-aware strategy funding helpers (Aged Care manual control slice 1).
 * Ported from frontend/src/utils/entityFundingUtils.js — deliberately a MINIMAL subset: only the
 * exports actually consumed by calculations/entityDisposalTax.ts, calculations/
 * retirementVillageCalculations.ts, calculation/assetLiquidation.ts (the server-side duplicate of
 * strategy/StrategyIntegrationUtils.js's `liquidateChosenAssets`), and
 * calculation/suggestionEngine/downsizer.ts (the server-side duplicate of features/supportAtHome/
 * supportAtHomeFactFindMutators.js's `applyDownsizeToFactFind`, which needs
 * `debitFromPersonalBankFirst`). Everything else in the frontend file (RAD-slider grouping helpers,
 * the suggestion-engine funding disclaimer, etc.) is UI-only and was never a dependency of the
 * calculation engine.
 *
 * Note: this file and ../entityDisposalTax.ts import from each other (resolveRadFundingEntry needs
 * calculateSharePartialDisposalTax/parseCgtOverride; entityDisposalTax needs findAssetInFactFind/
 * parseRadFundingAssetKey from here). This is a pre-existing circular import in the original
 * frontend code — both symbols are only referenced inside function bodies, never at module-eval
 * time, so ESM's live bindings resolve it safely. Do not try to "fix" it; the golden master proves
 * behavior either way.
 */
import { isEntityOwnership, getEntityIdFromOwnership, entityOwnershipValue } from '../constants/clientStructureTypes.ts';
import { calculateSharePartialDisposalTax, parseCgtOverride } from '../entityDisposalTax.ts';
import type { FactFind, FactFindAssetMap } from '../factFindTypes.ts';

export function assetIdsMatch(left: unknown, right: unknown): boolean {
    if (left == null || right == null) return false;
    return String(left) === String(right);
}

function findAssetIndexById(assetList: any[] | undefined, assetId: string | number): number {
    if (!Array.isArray(assetList)) return -1;
    return assetList.findIndex((a) => assetIdsMatch(a.id, assetId));
}

export function findAssetInFactFind(factFind: FactFind, assetType: string, assetId: string | number): { asset: any; index: number } | null {
    const list = factFind?.assets?.[assetType];
    const index = findAssetIndexById(list, assetId);
    if (index < 0) return null;
    return { asset: list?.[index], index };
}

/** @param key e.g. `bankAccounts-cash1` or `bankAccounts-123` */
export function parseRadFundingAssetKey(key: unknown): { assetType: string; assetId: string } | null {
    if (!key || typeof key !== 'string') return null;
    const dash = key.indexOf('-');
    if (dash <= 0) return null;
    const assetType = key.slice(0, dash);
    const assetId = key.slice(dash + 1);
    if (!assetType || !assetId) return null;
    return { assetType, assetId };
}

/**
 * Option A (net proceeds pool): gross liquidation vs net RAD contribution for one funding key.
 * Shares with cost base: net = gross − effective CGT; other assets: net = gross.
 */
export function resolveRadFundingEntry(
    factFind: FactFind,
    assetKey: string,
    grossAmount: number,
    cgtOverrides: Record<string, unknown> = {},
): { gross: number; cgt: number; net: number } {
    const gross = Math.max(0, grossAmount || 0);
    if (gross <= 0) {
        return { gross: 0, cgt: 0, net: 0 };
    }

    const parsed = parseRadFundingAssetKey(assetKey);
    if (!parsed || parsed.assetType !== 'shares') {
        return { gross, cgt: 0, net: gross };
    }

    const located = findAssetInFactFind(factFind, parsed.assetType, parsed.assetId);
    if (!located?.asset) {
        return { gross, cgt: 0, net: gross };
    }
    // NB: a missing cost base no longer means $0 CGT — calculateSharePartialDisposalTax applies the
    // shared 25%-of-value assumed cost base (resolveCostBase), disclosed in-app and in the SOA.

    const taxResult = calculateSharePartialDisposalTax(factFind, located.asset, gross, {
        trustDisposal: 'distribute',
    });
    const override = parseCgtOverride(cgtOverrides[assetKey]);
    const cgt = override != null ? override : taxResult.estimatedCGT ?? 0;
    return { gross, cgt, net: Math.max(0, gross - cgt) };
}

/**
 * Sum `resolveRadFundingEntry` across a whole RAD-funding map (manual `type-id` keys → gross amount).
 * Used by the aged-care suggestion engine to total up net RAD funded across possibly-many liquid
 * assets in one scenario.
 */
export function computeRadFundingTotals(
    factFind: FactFind,
    radFunding: Record<string, number> = {},
    cgtOverrides: Record<string, unknown> = {},
): { grossTotal: number; netTotal: number; shareCgtTotal: number; byKey: Record<string, { gross: number; cgt: number; net: number }> } {
    let grossTotal = 0;
    let netTotal = 0;
    let shareCgtTotal = 0;
    const byKey: Record<string, { gross: number; cgt: number; net: number }> = {};

    Object.entries(radFunding || {}).forEach(([key, amount]) => {
        const entry = resolveRadFundingEntry(factFind, key, amount, cgtOverrides);
        byKey[key] = entry;
        grossTotal += entry.gross;
        netTotal += entry.net;
        shareCgtTotal += entry.cgt;
    });

    return { grossTotal, netTotal, shareCgtTotal, byKey };
}

/** First personal/joint bank account (excludes entity-owned rows). */
function findPersonalBankAccount(assets: FactFindAssetMap): any {
    const list = assets?.bankAccounts;
    if (!Array.isArray(list) || list.length === 0) return null;
    return list.find((a) => !isEntityOwnership(a?.ownership)) || list[0];
}

/**
 * Debit CGT cash impact from the pool matching asset ownership (entity bank or personal).
 * Used by SAH/RV manual strategy paths when liquidating shares or investment property.
 */
export function debitCgtFromOwnershipPool(assets: FactFindAssetMap, cgtAmount: number, ownership: string | undefined): void {
    const cgt = Math.max(0, cgtAmount || 0);
    if (cgt <= 0 || !assets?.bankAccounts?.length) return;

    if (isEntityOwnership(ownership)) {
        const entityId = getEntityIdFromOwnership(ownership);
        const entityOwn = entityOwnershipValue(entityId as string);
        const entityBank = assets.bankAccounts.find((a: any) => a.ownership === entityOwn) || null;
        if (entityBank) {
            entityBank.value = Math.max(0, (entityBank.value || 0) - cgt);
            return;
        }
    }

    const personalBank = findPersonalBankAccount(assets);
    if (personalBank) {
        personalBank.value = Math.max(0, (personalBank.value || 0) - cgt);
    }
}

/** Debit an arbitrary amount (e.g. a "buying up" shortfall) from the first personal bank account. */
export function debitFromPersonalBankFirst(assets: FactFindAssetMap, amount: number): void {
    const debit = Math.max(0, amount || 0);
    if (debit <= 0 || !assets?.bankAccounts?.length) return;

    const personalBank = findPersonalBankAccount(assets);
    if (!personalBank) return;

    personalBank.value = Math.max(0, (personalBank.value || 0) - debit);
}
